import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import type { MetricWithObject } from "types/BusinessDashboard";
import type { MonthlyParams } from "api/businessDashboard";
import {
  SALES_PASS_TYPE_MAP,
  salesDashboardTabOptions,
} from "constants/businessDashboard";
import { useUpdateUrlParams } from "hooks/useUpdateUrlParams";
import {
  useSalesSummaryQuery,
  useTargetDataQuery,
} from "queries/Business/Dashboard/useDashboardQueries";

import { SalesStatCard } from "./sales-stat-card";
import { RefundCancelRow } from "./refund-cancel-row";
import { SalesDashboardSummarySkeleton } from "./sales-dashboard-summary.skeleton";
import { SalesDashboardSummaryError } from "./sales-dashboard-summary.error";

dayjs.locale("ko");

const Wrapper = styled.div`
  padding: 0 16px;
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`;

export const SalesDashboardSummary = () => {
  const { searchParams } = useUpdateUrlParams();
  const activeTab = searchParams.get("tab") || salesDashboardTabOptions[0].id;

  const referenceMonth = searchParams.get("month") || dayjs().format("YYYY-MM");

  //@ts-expect-error: __
  const partnerInfo = useSelector((state) => state.partner);

  const apiParams = useMemo(() => {
    const params: MonthlyParams = {
      referenceMonth,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [referenceMonth, partnerInfo]);
  const targetApiParams = useMemo(() => {
    const params: MonthlyParams = {
      referenceMonth,
    };

    const seqParam = partnerInfo.seqPartner;
    const branchSeqParam = partnerInfo.seqPartnerBranch;

    if (seqParam) {
      params.seqPartner = Number(seqParam);
    }

    if (branchSeqParam) {
      params.seqPartnerBranch = Number(branchSeqParam);
    }

    return params;
  }, [referenceMonth, partnerInfo]);

  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    isError: isSummaryError,
    refetch: refetchDetailData,
  } = useSalesSummaryQuery(apiParams);
  const { refetch: refetchTargetData } = useTargetDataQuery(targetApiParams, {
    enabled: false,
  });

  const handleRefresh = () => {
    refetchDetailData();
    refetchTargetData();
  };

  if (isSummaryLoading) return <SalesDashboardSummarySkeleton />;
  if (isSummaryError || !summaryData)
    return <SalesDashboardSummaryError onRetry={handleRefresh} />;

  // 탭 키 -> 지표. SALES_PASS_TYPE_MAP의 키와 1:1로 맞춘다.
  const stats: { [key: string]: MetricWithObject } = {
    total: summaryData.data.total,
    pt: summaryData.data.personalLesson,
    groupClass: summaryData.data.groupClass,
    membership: summaryData.data.membership,
    option: summaryData.data.option,
    transferCommission: summaryData.data.transferCommission,
    etc: summaryData.data.etc,
  };

  // URL의 tab이 맵에 없는 값이면 getSalesApiPassType이 TOTAL로 폴백해 표·차트는
  // 전체 매출을 그린다. 여기서도 같은 기준으로 맞춰야 카드만 사라지지 않는다.
  const tabKey = SALES_PASS_TYPE_MAP[activeTab] ? activeTab : "total";
  const tabInfo = SALES_PASS_TYPE_MAP[tabKey];
  const metric = stats[tabKey];
  const refundCancelMetric = summaryData.data.refundCancel;

  return (
    <Wrapper>
      {/*
        예전에는 탭별로 Object.entries(SALES_PASS_TYPE_MAP).slice(n, n+1)을 썼다.
        맵 중간에 분류를 추가하면 뒤쪽 탭의 인덱스가 밀려서 에러 없이 조용히
        다른 분류의 금액을 표시했다. 탭 키로 직접 조회한다.
      */}
      {metric && (
        <SalesStatCard
          key={tabKey}
          title={tabInfo.cardTitle}
          keyName={tabKey === "total" ? "monthlySales" : undefined}
          metric={metric}
          big
        />
      )}
      {/*
        환불·취소는 분류가 아니라 총매출 밖의 별도 값이다.
        이 화면은 탭당 카드 1개 구조라 분류 목록이 없으므로 전체 탭에만 둔다.
      */}
      {tabKey === "total" && refundCancelMetric && (
        <RefundCancelRow metric={refundCancelMetric} />
      )}
    </Wrapper>
  );
};
